import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import { useNavigationWithQuery } from '../authentication/useNavigationWithQuery';
import { mfaResignToken, validateMfaToken } from '../../lib/api';
import { useRedirectToClient } from '../authentication/useRedirectToClient';

import { useClient } from './useClient';

type LoginResponse = {
  mfaRequired: boolean;
  mfaEnabled: boolean;
  token: string;
};
export function useMFARequirementCheck(loginResponse?: LoginResponse) {
  const { mfaEnabled, mfaRequired, token } = loginResponse || {};
  const [validatingMFAToken, setValidatingMFAToken] = useState(false);
  const { mfaToken, redirectURL } = useClient();
  const { navigateTo } = useNavigationWithQuery();
  const { redirectToClient } = useRedirectToClient();
  const { status } = useQuery(
    ['validateMfaToken', mfaToken, token],
    ({ queryKey }) => {
      return validateMfaToken(queryKey[1], queryKey[2]);
    },
    {
      enabled: validatingMFAToken, // query only if mfatoken exists
    }
  );

  useEffect(() => {
    if (loginResponse && mfaRequired) {
      if (!mfaEnabled) {
        navigateTo({ path: 'mfa' });
      } else {
        // if there is an mfatoken update state to trigger mfatokenvalidate api call
        if (mfaToken) {
          setValidatingMFAToken(true);
        } else {
          navigateTo({ path: 'verifycode' });
        }
      }
    }
  }, [loginResponse]);

  useEffect(() => {
    if (validatingMFAToken) {
      // if user is logged in and mfatoken validated with success
      if (token && validatingMFAToken && status === 'success') {
        setValidatingMFAToken(false);
        /**
         * If token response is success, we need to resign the token with the mfa token
         */
        mfaResignToken({ mfaToken: mfaToken, accessToken: token })
          .then((resignedMfaTokenResponse) => {
            if (resignedMfaTokenResponse.result === 'error') {
              navigateTo({ path: 'verifycode' });
            }

            if (resignedMfaTokenResponse.result === 'success') {
              redirectToClient(redirectURL, resignedMfaTokenResponse.token);
            }
          })
          .catch((e) => {
            navigateTo({ path: 'verifycode' });
          });
        // if user is logged in and mfatoken validation failed
      } else if (token && status === 'error') {
        navigateTo({ path: 'verifycode' });
      }
    }
  }, [
    validatingMFAToken,
    status,
    token,
    redirectToClient,
    navigateTo,
    mfaToken,
    redirectURL,
  ]); // run if mfatokenvalidation is triggered
  return { validatingMFAToken, status };
}
